import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "@/components/atomic/Loading";
import { CategoricalAttributeFilter } from "@/components/ui/chat/attribute-filters/CategoricalAttributeFilter";
import { NumericalAttributeFilter } from "@/components/ui/chat/attribute-filters/NumericalAttributeFilter";
import { TextualAttributeFilter } from "@/components/ui/chat/attribute-filters/TextualAttributeFilter";
import { ChatAttributeFilter, ChatAttributeFilters } from "@/components/ui/chat/chatFilterUtils";
import { cn } from "@/lib/utils";
import { Attribute, AttributeType } from "@/models/attribute";

interface AttributesListProps {
  attributes: Attribute[] | undefined;
  isLoading: boolean;
  expandedAttributes: Set<string>;
  attributeFilters: ChatAttributeFilters;
  onToggleAttribute: (attributeName: string) => void;
  onFilterChange: (attributeName: string, filter: ChatAttributeFilter | null) => void;
}

export const AttributesList: React.FC<AttributesListProps> = ({
  attributes,
  isLoading,
  expandedAttributes,
  attributeFilters,
  onToggleAttribute,
  onFilterChange,
}) => {
  const { t } = useTranslation("voiceek_ui");

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!attributes || attributes.length === 0) {
    return (
      <p className="text-[14px] text-[#999999] pt-2.5">
        {t("chat.filter.noAttributes")}
      </p>
    );
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      {attributes.map((attribute) => {
        const isExpanded = expandedAttributes.has(attribute.name);
        return (
          <div key={attribute.name} className="flex flex-col">
            <button
              onClick={() => onToggleAttribute(attribute.name)}
              className={cn(
                "flex items-center gap-2 text-left transition-colors p-1 -ml-1 rounded",
                "hover:cursor-pointer",
              )}
            >
              <p className="text-[14px] font-semibold text-[#333333] leading-[20px]">
                {attribute.name}
              </p>
              {isExpanded
                ? <IconChevronDown className="w-4 h-4 text-[#333333]" stroke={2} />
                : (
                  <IconChevronRight
                    className="w-4 h-4 text-[#333333] group-hover:translate-x-0.5 transition-transform"
                    stroke={2}
                  />
                )}
            </button>

            <div
              className={cn(
                "grid transition-all duration-200 ease-in-out",
                isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
              )}
            >
              <div className="overflow-hidden">
                {attribute.type === AttributeType.CATEGORICAL && (
                  <CategoricalAttributeFilter
                    attribute={attribute}
                    currentFilter={attributeFilters[attribute.name]}
                    onFilterChange={onFilterChange}
                  />
                )}
                {attribute.type === AttributeType.NUMERICAL && (
                  <NumericalAttributeFilter
                    attribute={attribute}
                    currentFilter={attributeFilters[attribute.name]}
                    onFilterChange={onFilterChange}
                  />
                )}
                {attribute.type === AttributeType.TEXTUAL && (
                  <TextualAttributeFilter
                    attribute={attribute}
                    currentFilter={attributeFilters[attribute.name]}
                    onFilterChange={onFilterChange}
                  />
                )}
                <div className="h-px bg-[#dddddd]" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
